import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, DatePicker, Grid, Select, Table, Tag, Tooltip, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { extractList, formatDateTime, formatNumber } from 'pages/star-shared/utils';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { formatData } from 'utils/common';
import { useDataImport } from '../context';
import { ImportLog, LogKind } from '../types';
import {
  LOAD_FAILED_TEXT,
  LOG_KIND_META,
  downloadFile,
  formatDay,
  formatFileSize,
  formatPeriod,
  parseLogMeta,
} from '../utils';
import styles from '../style.module.scss';

const PAGE_SIZE = 20;

const KIND_OPTIONS = (Object.keys(LOG_KIND_META) as LogKind[]).map((kind) => ({
  value: kind,
  label: LOG_KIND_META[kind].label,
}));

const isFileKind = (kind: LogKind) => kind === 'DAILY' || kind === 'MONTHLY';

// Cấp quyền / Thu quyền không gắn với giai đoạn số liệu nào (máy chủ để trống cột tháng),
// nên lọc theo tháng không bao giờ ra hai loại này.
const hasNoPeriod = (kind?: LogKind) => kind === 'GRANT' || kind === 'REVOKE';

/** Cột "Ghi chú": lý do người nhập ghi + phần máy tự ghi theo từng loại. */
const renderNote = (log: ImportLog) => {
  const lines: string[] = [];
  const meta = parseLogMeta(log.meta);
  const targetName = log.targetUserName || meta.targetName;
  const target = targetName
    ? `${targetName} (#${log.targetUserId})`
    : log.targetUserId
    ? `tài khoản #${log.targetUserId}`
    : '';
  if (log.kind === 'MANUAL') {
    const before =
      meta.before === null || meta.before === undefined ? 'chưa có' : formatNumber(meta.before);
    if (meta.date || meta.after !== undefined) {
      lines.push(
        `KC ngày ${formatDay(meta.date)}${
          target ? ` của ${target}` : ''
        }: ${before} → ${formatNumber(meta.after)}`
      );
    } else if (target) {
      lines.push(`Nhập tay cho ${target}`);
    }
  } else if (log.kind === 'GRANT' || log.kind === 'REVOKE') {
    if (target) lines.push(`${log.kind === 'GRANT' ? 'Cấp cho' : 'Thu của'} ${target}`);
  } else {
    const parts = [
      log.linkedCount ? `gắn UID mới ${formatNumber(log.linkedCount)}` : '',
      log.unmatchedCount ? `chưa có tài khoản ${formatNumber(log.unmatchedCount)}` : '',
      log.departedCount
        ? `đã rời đại lý ${formatNumber(log.departedCount)} (${formatNumber(
            log.departedDiamonds
          )} KC)`
        : '',
    ].filter(Boolean);
    if (parts.length) lines.push(parts.join(' · '));
  }
  if (!log.note && !lines.length) return '—';
  return (
    <div className={styles.small}>
      {log.note && <div>{log.note}</div>}
      {lines.map((line) => (
        <div key={line} className={styles.personSub}>
          {line}
        </div>
      ))}
    </div>
  );
};

/**
 * Tab "Lịch sử nhập": mọi lần nhập file, nhập tay, cấp / thu quyền. ADMIN thấy tất cả và
 * tải được file gốc; MOD chỉ thấy lần nhập của chính mình (backend lọc).
 */
const HistoryTab = () => {
  const { isAdmin, reportError } = useDataImport();
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState<LogKind | undefined>(undefined);
  const [month, setMonth] = useState<string | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  const [items, setItems] = useState<ImportLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // Lần tải gần nhất bị lỗi: bảng ghi "chưa tải được" thay vì "chưa có lần nhập nào".
  const [failed, setFailed] = useState(false);
  const [downloading, setDownloading] = useState<number | null>(null);
  // Bộ lọc + trang của các dòng đang hiện trong bảng.
  const loadedQuery = useRef('');

  useEffect(() => {
    let cancelled = false;
    const query = `${page}|${kind || ''}|${month || ''}`;
    setLoading(true);
    setFailed(false);
    AxiosUtils.get(API.dataImportLogs, formatData({ page, limit: PAGE_SIZE, kind, month }))
      .then((res: any) => {
        if (cancelled) return;
        const next = extractList<ImportLog>(res);
        loadedQuery.current = query;
        setItems(next.items);
        setTotal(next.total);
      })
      .catch((err) => {
        if (cancelled) return;
        // Bấm Tải lại mà lỗi: giữ các dòng đang hiện (vẫn đúng bộ lọc). Đổi bộ lọc / trang mà
        // lỗi: bỏ các dòng cũ vì chúng không khớp bộ lọc đang chọn.
        if (loadedQuery.current !== query) {
          loadedQuery.current = '';
          setItems([]);
          setTotal(0);
        }
        setFailed(true);
        reportError(err, 'Không tải được lịch sử nhập');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, kind, month, refreshKey, reportError]);

  const download = async (log: ImportLog) => {
    setDownloading(log.id);
    try {
      await downloadFile(
        `${API.dataImportLogs}/${log.id}/download`,
        log.fileName || `nhap-du-lieu-${log.id}.xlsx`
      );
    } catch (err) {
      reportError(err, 'Không tải được file gốc');
    } finally {
      setDownloading(null);
    }
  };

  // Màn hẹp: nút tải chỉ còn biểu tượng để cột ghim bên phải không chiếm nửa màn hình.
  const compact = Grid.useBreakpoint().md === false;

  // MỌI cột đều có bề rộng (cột "Ghi chú" từng không có → chỉ còn ~40px, mỗi dòng cao
  // cả trăm px). Bảng cuộn ngang trong khung riêng khi màn hình hẹp hơn tổng bề rộng.
  // Bảy cột đầu cộng lại 966px: ở màn 1440 chúng vừa khít phần nhìn thấy cạnh cột ghim.
  const columns: ColumnsType<ImportLog> = [
    {
      title: 'Thời gian',
      key: 'createdAt',
      width: 150,
      render: (_, log) => formatDateTime(log.createdAt || log.created_at),
    },
    {
      title: 'Loại',
      dataIndex: 'kind',
      width: 105,
      render: (value: LogKind) => {
        const meta = LOG_KIND_META[value];
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : <Tag>{value}</Tag>;
      },
    },
    {
      title: 'Giai đoạn',
      key: 'period',
      // "01/04/2019 → 30/04/2019" phải nằm trên một dòng.
      width: 216,
      render: (_, log) => formatPeriod(log.periodStart, log.periodEnd),
    },
    {
      title: 'File',
      key: 'file',
      width: 210,
      render: (_, log) =>
        log.fileName ? (
          <div>
            <Tooltip title={log.fileName}>
              <Typography.Text ellipsis style={{ maxWidth: 190 }}>
                {log.fileName}
              </Typography.Text>
            </Tooltip>
            {!!log.fileSize && (
              <div className={styles.personSub}>{formatFileSize(log.fileSize)}</div>
            )}
          </div>
        ) : (
          '—'
        ),
    },
    {
      title: 'Số dòng',
      key: 'rowCount',
      align: 'right',
      width: 80,
      render: (_, log) => (isFileKind(log.kind) ? formatNumber(log.rowCount || 0) : '—'),
    },
    {
      title: 'Creator',
      key: 'creatorCount',
      align: 'right',
      width: 80,
      render: (_, log) => (isFileKind(log.kind) ? formatNumber(log.creatorCount || 0) : '—'),
    },
    {
      title: 'Tổng KC',
      key: 'diamondsTotal',
      align: 'right',
      width: 125,
      render: (_, log) =>
        log.kind === 'GRANT' || log.kind === 'REVOKE' ? '—' : formatNumber(log.diamondsTotal || 0),
    },
    {
      title: 'Người nhập',
      key: 'importedBy',
      width: 160,
      render: (_, log) => log.importedByName || (log.importedById ? `#${log.importedById}` : '—'),
    },
    {
      title: 'Ghi chú',
      key: 'note',
      // Màn hẹp: 280px để cả cột nằm gọn trong phần nhìn thấy bên cạnh cột ghim.
      width: compact ? 280 : 320,
      render: (_, log) => renderNote(log),
    },
  ];

  if (isAdmin) {
    // Ghim bên phải: nút tải luôn trong tầm mắt dù bảng đang cuộn ngang.
    columns.push({
      title: compact ? '' : 'File gốc',
      key: 'download',
      width: compact ? 56 : 150,
      fixed: 'right',
      render: (_, log) =>
        log.hasFile ? (
          <Tooltip title={compact ? 'Tải file gốc' : undefined}>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={downloading === log.id}
              aria-label={`Tải file gốc ${log.fileName || `#${log.id}`}`}
              onClick={() => download(log)}
            >
              {compact ? null : 'Tải file gốc'}
            </Button>
          </Tooltip>
        ) : null,
    });
  }

  const tableWidth = columns.reduce((sum, column) => sum + (Number(column.width) || 0), 0);

  return (
    <div>
      <div className={styles.toolbar}>
        <Select
          allowClear
          placeholder="Tất cả loại"
          style={{ width: 170 }}
          value={kind}
          // MOD chỉ thấy lần nhập của chính mình nên không bao giờ có dòng Cấp / Thu quyền.
          options={isAdmin ? KIND_OPTIONS : KIND_OPTIONS.filter((o) => !hasNoPeriod(o.value))}
          onChange={(value) => {
            setKind(value);
            // Hai loại này không có giai đoạn: bỏ lọc tháng, nếu không bảng luôn trống.
            if (hasNoPeriod(value)) setMonth(undefined);
            setPage(1);
          }}
        />
        <DatePicker
          picker="month"
          format="MM/YYYY"
          placeholder="Tháng của số liệu"
          aria-label="Lọc theo tháng của số liệu"
          style={{ width: 190 }}
          disabled={hasNoPeriod(kind)}
          value={month ? dayjs(`${month}-01`) : null}
          onChange={(date) => {
            setMonth(date ? date.format('YYYY-MM') : undefined);
            setPage(1);
          }}
        />
        <Button
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={() => setRefreshKey((key) => key + 1)}
        >
          Tải lại
        </Button>
      </div>
      <p className={styles.note}>
        {!isAdmin && 'Bạn chỉ thấy các lần nhập của chính mình. '}
        Lọc tháng theo giai đoạn của số liệu, không theo ngày thực hiện.
        {isAdmin &&
          ' Cấp quyền / Thu quyền không có giai đoạn nên không hiện khi lọc theo tháng.'}
      </p>
      {failed && items.length > 0 && (
        <Alert
          className={styles.section}
          type="warning"
          showIcon
          message="Chưa tải lại được. Bảng đang hiện kết quả của lần tải trước."
        />
      )}
      <div className="custom-table">
        <Table
          size="small"
          rowKey="id"
          loading={loading}
          dataSource={items}
          columns={columns}
          scroll={{ x: tableWidth }}
          locale={{
            emptyText: failed
              ? LOAD_FAILED_TEXT
              : kind || month
              ? 'Không có dòng nào khớp bộ lọc.'
              : 'Chưa có lần nhập nào.',
          }}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            showLessItems: true,
            onChange: setPage,
          }}
        />
      </div>
    </div>
  );
};

export default HistoryTab;
